import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ListingsService } from '@features/listings/services/listings.service';
import {
  FilterOptions,
  SelectedBrand,
  ServiceOption,
} from '@shared/models/filter.models';
import { Option } from '@shared/models/ui.models';

export interface SelectedFilter {
  brands: SelectedBrand[];
  isPrivateCustomer: boolean | null; //
  leasingType: Option;
  services: ServiceOption;
  price_monthly_min: number | null;
  price_monthly_max: number | null;
  power_in_hp_min: number | null;
  power_in_hp_max: number | null;
  total_weight_in_kg: number | null;
  gear_type: string | null;
  bodywork: number[];
  dealer_id: number[] | null;
  period: number | null;
  down_payment_min: number | null;
  down_payment_max: number | null;
  minYear: number | null;
  maxYear: number | null;
  minMileage: number | null;
  maxMileage: number | null;
  kilometers_min: number | null;
  kilometers_max: number | null;
  fuel_type: number[];
  seats: number | null;
  doors: number | null;
  equipment: string[];
}

@Injectable({
  providedIn: 'root',
})
export class FilterStoreService {
  // #### Shared types
  leasingtypes: Option[] = [
    { name: 'Alle', value: null },
    { name: 'Operationelt leasing', value: '0' },
    { name: 'Finansielt leasing', value: '1' },
    { name: 'Månedsleje', value: '6' }, // This is leasing_type in endpoint and not fundiing_id like the rest
  ];

  // Method to create fresh default filter object
  getDefaultSelectedFilter(): SelectedFilter {
    return {
      isPrivateCustomer: null,
      leasingType: { name: 'Alle', value: null },
      services: { name: '', value: 0 },
      brands: [],
      minYear: null,
      maxYear: null,
      minMileage: null,
      maxMileage: null,
      price_monthly_min: null,
      price_monthly_max: null,
      power_in_hp_min: null,
      power_in_hp_max: null,
      total_weight_in_kg: null,
      gear_type: null,
      bodywork: [],
      dealer_id: null,
      period: null,
      down_payment_min: null,
      down_payment_max: null,
      kilometers_min: null,
      kilometers_max: null,
      fuel_type: [],
      seats: null,
      doors: null,
      equipment: [],
    };
  }

  filterSubject = new BehaviorSubject<SelectedFilter>(
    this.getDefaultSelectedFilter(),
  );

  selectedFilter$ = this.filterSubject.asObservable();

  // Get current filter
  get currentFilter(): SelectedFilter {
    return this.filterSubject.value;
  }

  // Update the whole filter
  updateFilter(newFilter: SelectedFilter) {
    this.filterSubject.next(newFilter);
  }

  createFilter(
    partial: Partial<SelectedFilter>,
    base: SelectedFilter = this.getDefaultSelectedFilter(),
  ): SelectedFilter {
    return {
      ...base,
      ...partial,
    };
  }

  reset() {
    this.filterSubject.next(this.getDefaultSelectedFilter());
  }

  // "Partial" update lol
  patchFilter(partial: Partial<SelectedFilter>) {
    this.filterSubject.next(this.createFilter(partial, this.currentFilter));
  }

  setBrands(brands: SelectedBrand[]) {
    this.patchFilter({ brands });
  }

  setLeasingType(leasingType: Option) {
    this.patchFilter({ leasingType });
  }

  setServices(services: ServiceOption) {
    this.patchFilter({ services });
  }

  setCustomerType(isPrivateCustomer: boolean | null) {
    this.patchFilter({ isPrivateCustomer });
  }

  setYearRange(minYear: number | null, maxYear: number | null) {
    this.patchFilter({ minYear, maxYear });
  }

  setPriceRange(
    price_monthly_min: number | null,
    price_monthly_max: number | null,
  ) {
    this.patchFilter({ price_monthly_min, price_monthly_max });
  }

  setMileageRange(minMileage: number | null, maxMileage: number | null) {
    this.patchFilter({ minMileage, maxMileage });
  }

  setDownPaymentRange(
    down_payment_min: number | null,
    down_payment_max: number | null,
  ) {
    this.patchFilter({ down_payment_min, down_payment_max });
  }

  setKilometerRange(
    kilometers_min: number | null,
    kilometers_max: number | null,
  ) {
    this.patchFilter({ kilometers_min, kilometers_max });
  }

  setPowerRange(
    power_in_hp_min: number | null,
    power_in_hp_max: number | null,
  ) {
    this.patchFilter({ power_in_hp_min, power_in_hp_max });
  }

  setBodywork(bodywork: number[]) {
    this.patchFilter({ bodywork });
  }

  setFuelType(fuel_type: number[]) {
    this.patchFilter({ fuel_type });
  }

  setGearType(gear_type: string | null) {
    this.patchFilter({ gear_type });
  }

  setPeriod(period: number | null) {
    this.patchFilter({ period });
  }

  setSeats(seats: number | null) {
    this.patchFilter({ seats });
  }

  setDoors(doors: number | null) {
    this.patchFilter({ doors });
  }

  setEquipment(equipment: string[]) {
    this.patchFilter({ equipment });
  }

  applySearchFilters(partial: Partial<SelectedFilter>) {
    this.patchFilter(partial);
  }

  //  ##############################
  //  ###### FILTER OPTIONS ########
  //  ##############################
  private filterOptionsSubject = new BehaviorSubject<FilterOptions | null>(
    null,
  );

  filterOptions$ = this.filterOptionsSubject.asObservable();

  get currentFilterOptions(): FilterOptions | null {
    return this.filterOptionsSubject.value;
  }

  setFilterOptions(options: FilterOptions) {
    this.filterOptionsSubject.next(options);
  }

  constructor(private listingsService: ListingsService) {
    this.listingsService.getFilter().subscribe((filters) => {
      this.setFilterOptions(filters);
    });
  }
}
